import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  getDocsFromServer,
  limitToLast,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  type Firestore,
  type Transaction,
  type Unsubscribe,
} from 'firebase/firestore'
import {
  bettingRound as currentBettingRound,
  gamePhase as currentGamePhase,
  generateRecoveryCode,
  generateRoomCode,
  hashRecoveryCode,
  normalizeRecoveryCode,
  normalizeRoomCode,
} from './lib/domain'
import { pickBilingual, text } from './i18n'
import type { ActionType, Game, GameAction, Member, Player } from './types'

const MAX_CREATE_ATTEMPTS = 8
export const MAX_PLAYERS = 10

export interface CreateGameInput {
  gameName: string
  displayName: string
  startingStack: number
}

export interface JoinGameInput {
  code: string
  displayName: string
}

export interface RecoverTableInput {
  code: string
  displayName: string
  recoveryCode: string
}

interface ActionInput {
  type: ActionType
  actorUid: string
  actorName: string
  targetUid?: string
  targetName?: string
  amount?: number
  playerCount?: number
  handNumber: number
  reversesActionId?: string
}

function cleanName(value: string): string {
  return value.trim().replace(/\s+/g, ' ').slice(0, 30)
}

function cleanGameName(value: string): string {
  return value.trim().replace(/\s+/g, ' ').slice(0, 40)
}

function actionData(input: ActionInput) {
  return {
    type: input.type,
    actorUid: input.actorUid,
    actorName: input.actorName,
    targetUid: input.targetUid ?? '',
    targetName: input.targetName ?? '',
    amount: input.amount ?? 0,
    ...(input.playerCount == null ? {} : { playerCount: input.playerCount }),
    handNumber: input.handNumber,
    reversesActionId: input.reversesActionId ?? '',
    createdAt: serverTimestamp(),
  }
}

function isMatchingContributionAction(action: GameAction, uid: string, amount: number): boolean {
  return action.type === 'bet'
    && action.actorUid === uid
    && action.targetUid === uid
    && action.amount === amount
}

function gameRef(db: Firestore, code: string) {
  return doc(db, 'games', normalizeRoomCode(code))
}

function memberRef(db: Firestore, code: string, uid: string) {
  return doc(db, 'games', normalizeRoomCode(code), 'members', uid)
}

function playerRef(db: Firestore, code: string, uid: string) {
  return doc(db, 'games', normalizeRoomCode(code), 'players', uid)
}

function playersQuery(db: Firestore, code: string) {
  return query(collection(db, 'games', normalizeRoomCode(code), 'players'), orderBy('joinedAt', 'asc'))
}

function newActionRef(db: Firestore, code: string, operationId?: string) {
  return operationId
    ? doc(db, 'games', normalizeRoomCode(code), 'actions', operationId)
    : doc(collection(db, 'games', normalizeRoomCode(code), 'actions'))
}

function controlRef(db: Firestore, code: string) {
  return doc(db, 'games', normalizeRoomCode(code), 'private', 'control')
}

function recoveryClaimRef(db: Firestore, code: string, actionId: string) {
  return doc(db, 'games', normalizeRoomCode(code), 'recoveryClaims', actionId)
}

export async function createGame(
  db: Firestore,
  uid: string,
  input: CreateGameInput,
): Promise<{ code: string; member: Member; recoveryCode: string }> {
  const displayName = cleanName(input.displayName)
  const name = cleanGameName(input.gameName)
  if (!displayName || !name) throw new Error('Enter a game name and display name · הזינו שם משחק ושם תצוגה.')
  if (!Number.isSafeInteger(input.startingStack) || input.startingStack < 100 || input.startingStack > 1_000_000) {
    throw new Error('Starting chips must be a whole number from 100 to 1,000,000 · כמות הפתיחה צריכה להיות מספר שלם בין 100 ל־1,000,000.')
  }
  const recoveryCode = generateRecoveryCode()
  const recoveryHash = await hashRecoveryCode(recoveryCode)

  for (let attempt = 0; attempt < MAX_CREATE_ATTEMPTS; attempt += 1) {
    const code = generateRoomCode()
    const room = gameRef(db, code)
    const member = memberRef(db, code, uid)
    const action = newActionRef(db, code)
    const control = controlRef(db, code)

    try {
      await runTransaction(db, async (transaction) => {
        const existingRoom = await transaction.get(room)
        if (existingRoom.exists()) throw new Error('ROOM_CODE_COLLISION')

        transaction.set(room, {
          code,
          name,
          tableUid: uid,
          startingStack: input.startingStack,
          pot: 0,
          handNumber: 1,
          handOpen: false,
          phase: 'lobby',
          leaderUid: '',
          playerCount: 0,
          bettingRound: 1,
          status: 'open',
          lastActionId: action.id,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
        transaction.set(member, {
          uid,
          displayName,
          role: 'table',
          isPlayer: false,
          joinedAt: serverTimestamp(),
        })
        transaction.set(control, {
          recoveryHash,
          updatedAt: serverTimestamp(),
        })
        transaction.set(
          action,
          actionData({
            type: 'create_game',
            actorUid: uid,
            actorName: displayName,
            targetUid: '',
            targetName: '',
            amount: input.startingStack,
            handNumber: 1,
          }),
        )
      })

      return {
        code,
        recoveryCode,
        member: {
          uid,
          displayName,
          role: 'table',
          isPlayer: false,
          joinedAt: null,
        },
      }
    } catch (error) {
      if (error instanceof Error && error.message === 'ROOM_CODE_COLLISION') continue
      throw error
    }
  }

  throw new Error('Could not create a unique room code; try again · לא הצלחנו ליצור קוד חדר ייחודי; נסו שוב.')
}

export async function joinGame(
  db: Firestore,
  uid: string,
  input: JoinGameInput,
): Promise<Member> {
  const code = normalizeRoomCode(input.code)
  const displayName = cleanName(input.displayName)
  if (code.length !== 4) throw new Error('Room code must contain 4 characters · קוד החדר צריך להכיל 4 תווים.')
  if (!displayName) throw new Error('Enter a display name · הזינו שם תצוגה.')

  const room = gameRef(db, code)
  const member = memberRef(db, code, uid)
  const player = playerRef(db, code, uid)
  const action = newActionRef(db, code)

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return await runTransaction(db, async (transaction) => {
        const [roomSnapshot, memberSnapshot] = await Promise.all([
          transaction.get(room),
          transaction.get(member),
        ])
        if (!roomSnapshot.exists()) throw new Error('No game found for this code · לא נמצא משחק עם הקוד הזה.')
        if (memberSnapshot.exists()) return memberSnapshot.data() as Member

        const game = roomSnapshot.data() as Game
        if (game.status !== 'open' || currentGamePhase(game) !== 'lobby') {
          throw new Error('This lobby has already started · הלובי הזה כבר התחיל.')
        }
        if ((game.playerCount ?? 0) >= MAX_PLAYERS) {
          throw new Error(`This table is full (${MAX_PLAYERS} players) · השולחן מלא (${MAX_PLAYERS} שחקנים).`)
        }
        const memberData: Omit<Member, 'joinedAt'> & { joinedAt: ReturnType<typeof serverTimestamp> } = {
          uid,
          displayName,
          role: 'player',
          isPlayer: true,
          joinedAt: serverTimestamp(),
        }

        transaction.set(member, memberData)
        transaction.set(player, {
          uid,
          name: displayName,
          balance: game.startingStack,
          foldedHand: null,
          contributionHand: game.handNumber,
          handContribution: 0,
          contributionRound: currentBettingRound(game),
          roundContribution: 0,
          joinedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
        transaction.update(room, {
          handOpen: game.handOpen !== false,
          leaderUid: game.leaderUid || uid,
          playerCount: (game.playerCount ?? 0) + 1,
          lastActionId: action.id,
          updatedAt: serverTimestamp(),
        })
        transaction.set(
          action,
          actionData({
            type: 'join',
            actorUid: uid,
            actorName: displayName,
            targetUid: uid,
            targetName: displayName,
            amount: game.startingStack,
            handNumber: game.handNumber,
          }),
        )

        return { ...memberData, joinedAt: null }
      })
    } catch (error) {
      const errorCode = (error as { code?: string }).code
      const retryable = errorCode === 'permission-denied' || errorCode === 'aborted'
      if (!retryable) throw error

      // A concurrent join can make a rules re-evaluation look like a permission
      // failure. If this client actually committed, treat it as success;
      // otherwise retry from the latest leader and player count.
      const existingMember = await getDoc(member)
      if (existingMember.exists()) return existingMember.data() as Member
      if (attempt === 2) throw error
    }
  }

  throw new Error('Could not join this lobby; try again · לא הצלחנו להצטרף ללובי; נסו שוב.')
}

export function listenToGame(
  db: Firestore,
  code: string,
  onValue: (game: Game | null, fromCache: boolean) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    gameRef(db, code),
    { includeMetadataChanges: true },
    (snapshot) => onValue(snapshot.exists() ? (snapshot.data() as Game) : null, snapshot.metadata.fromCache),
    onError,
  )
}

export function listenToMember(
  db: Firestore,
  code: string,
  uid: string,
  onValue: (member: Member | null, fromCache: boolean) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    memberRef(db, code, uid),
    { includeMetadataChanges: true },
    (snapshot) => onValue(snapshot.exists() ? (snapshot.data() as Member) : null, snapshot.metadata.fromCache),
    onError,
  )
}

export function listenToPlayers(
  db: Firestore,
  code: string,
  onValue: (players: Player[], fromCache: boolean) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    playersQuery(db, code),
    { includeMetadataChanges: true },
    (snapshot) => onValue(snapshot.docs.map((item) => item.data() as Player), snapshot.metadata.fromCache),
    onError,
  )
}

/**
 * Bypass the local Firestore cache for the two documents/queries that drive the
 * live table. Realtime listeners remain the primary path; this is a small
 * reconciliation escape hatch for browsers that occasionally leave a WebChannel
 * listener alive but stop delivering remote changes promptly.
 */
export async function readGameFromServer(db: Firestore, code: string): Promise<Game | null> {
  const snapshot = await getDocFromServer(gameRef(db, code))
  return snapshot.exists() ? (snapshot.data() as Game) : null
}

export async function readPlayersFromServer(db: Firestore, code: string): Promise<Player[]> {
  const snapshot = await getDocsFromServer(playersQuery(db, code))
  return snapshot.docs.map((item) => item.data() as Player)
}

export function listenToActions(
  db: Firestore,
  code: string,
  onValue: (actions: GameAction[], fromCache: boolean) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  const actionsQuery = query(
    collection(db, 'games', normalizeRoomCode(code), 'actions'),
    orderBy('createdAt', 'asc'),
    limitToLast(120),
  )
  return onSnapshot(
    actionsQuery,
    { includeMetadataChanges: true },
    (snapshot) => onValue(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }) as GameAction), snapshot.metadata.fromCache),
    onError,
  )
}

async function readActor(
  transaction: Transaction,
  db: Firestore,
  code: string,
  uid: string,
): Promise<Member> {
  const snapshot = await transaction.get(memberRef(db, code, uid))
  if (!snapshot.exists()) throw new Error('Room membership is inactive · החברות בחדר אינה פעילה.')
  return snapshot.data() as Member
}

function canControlGame(game: Game, uid: string): boolean {
  return game.tableUid === uid || game.leaderUid === uid
}

function requirePlaying(game: Game) {
  if (currentGamePhase(game) !== 'playing') {
    throw new Error('Wait for the Leader to start · המתינו שהמוביל/ה יתחיל/תתחיל.')
  }
}

export async function startGame(db: Firestore, code: string, uid: string): Promise<void> {
  const room = gameRef(db, code)
  const action = newActionRef(db, code)

  await runTransaction(db, async (transaction) => {
    const [roomSnapshot, actor] = await Promise.all([
      transaction.get(room),
      readActor(transaction, db, code, uid),
    ])
    if (!roomSnapshot.exists()) throw new Error('Room no longer exists · החדר כבר אינו קיים.')
    const game = roomSnapshot.data() as Game
    if (!canControlGame(game, uid)) throw new Error('Only the Leader can start · רק המוביל/ה יכול/ה להתחיל.')
    if (currentGamePhase(game) !== 'lobby') throw new Error('The game has already started · המשחק כבר התחיל.')
    if ((game.playerCount ?? 0) < 2) throw new Error('At least two players must join · לפחות שני שחקנים צריכים להצטרף.')
    if (game.pot !== 0) throw new Error('The lobby cannot start with chips in the pot · אי אפשר להתחיל לובי עם צ׳יפים בקופה.')

    transaction.update(room, {
      phase: 'playing',
      handOpen: true,
      bettingRound: 1,
      lastActionId: action.id,
      updatedAt: serverTimestamp(),
    })
    transaction.set(action, actionData({
      type: 'start_game',
      actorUid: uid,
      actorName: actor.displayName,
      playerCount: game.playerCount ?? 0,
      handNumber: game.handNumber,
    }))
  })
}

export async function setLeader(
  db: Firestore,
  code: string,
  uid: string,
  targetUid: string,
  expectedLeaderUid: string,
): Promise<void> {
  const room = gameRef(db, code)
  const target = playerRef(db, code, targetUid)
  const action = newActionRef(db, code)

  await runTransaction(db, async (transaction) => {
    const [roomSnapshot, targetSnapshot, actor] = await Promise.all([
      transaction.get(room),
      transaction.get(target),
      readActor(transaction, db, code, uid),
    ])
    if (!roomSnapshot.exists()) throw new Error('Room no longer exists · החדר כבר אינו קיים.')
    if (!targetSnapshot.exists()) throw new Error('Selected player was not found · השחקן שנבחר לא נמצא.')
    const game = roomSnapshot.data() as Game
    const targetPlayer = targetSnapshot.data() as Player
    if (game.status !== 'open') throw new Error('This room is no longer active · החדר הזה כבר לא פעיל.')
    if (game.tableUid !== uid) throw new Error('Only the table device can change the Leader · רק מכשיר השולחן יכול להחליף מוביל/ה.')
    if ((game.leaderUid ?? '') !== expectedLeaderUid) throw new Error('The Leader already changed; choose again · המוביל/ה כבר התחלף/ה; בחרו שוב.')
    if (game.leaderUid === targetUid) return

    transaction.update(room, {
      leaderUid: targetUid,
      lastActionId: action.id,
      updatedAt: serverTimestamp(),
    })
    transaction.set(action, actionData({
      type: 'set_leader',
      actorUid: uid,
      actorName: actor.displayName,
      targetUid,
      targetName: targetPlayer.name,
      handNumber: game.handNumber,
    }))
  })
}

export async function restartGame(
  db: Firestore,
  code: string,
  uid: string,
  confirmedCode: string,
): Promise<void> {
  const normalizedCode = normalizeRoomCode(code)
  if (normalizeRoomCode(confirmedCode) !== normalizedCode) {
    throw new Error('Type the current 4-character room code to confirm · הקלידו את קוד החדר הנוכחי בן 4 התווים לאישור.')
  }

  const room = gameRef(db, normalizedCode)
  const action = newActionRef(db, normalizedCode)
  const listedPlayers = await getDocs(collection(db, 'games', normalizedCode, 'players'))
  if (listedPlayers.size > MAX_PLAYERS) throw new Error(`Restart supports up to ${MAX_PLAYERS} players · ההתחלה מחדש תומכת בעד ${MAX_PLAYERS} שחקנים.`)
  const playerDocuments = listedPlayers.docs.map((snapshot) => playerRef(db, normalizedCode, snapshot.id))

  await runTransaction(db, async (transaction) => {
    const [roomSnapshot, actor, ...playerSnapshots] = await Promise.all([
      transaction.get(room),
      readActor(transaction, db, normalizedCode, uid),
      ...playerDocuments.map((reference) => transaction.get(reference)),
    ])
    if (!roomSnapshot.exists()) throw new Error('Room no longer exists · החדר כבר אינו קיים.')
    const game = roomSnapshot.data() as Game
    requirePlaying(game)
    if (!canControlGame(game, uid)) throw new Error('Only the Leader or table can restart · רק המוביל/ה או השולחן יכולים להתחיל מחדש.')

    const currentPlayers = playerSnapshots.filter((snapshot) => snapshot.exists())
    if (currentPlayers.length !== (game.playerCount ?? currentPlayers.length)) {
      throw new Error('The player list changed; try restart again · רשימת השחקנים השתנתה; נסו להתחיל מחדש שוב.')
    }
    const currentLeaderExists = currentPlayers.some((snapshot) => snapshot.id === game.leaderUid)
    const leaderUid = currentLeaderExists ? game.leaderUid! : (currentPlayers[0]?.id ?? '')

    playerSnapshots.forEach((snapshot, index) => {
      if (!snapshot.exists()) return
      transaction.update(playerDocuments[index], {
        balance: game.startingStack,
        foldedHand: null,
        contributionHand: 1,
        handContribution: 0,
        contributionRound: 1,
        roundContribution: 0,
        updatedAt: serverTimestamp(),
      })
    })
    transaction.update(room, {
      phase: 'lobby',
      leaderUid,
      playerCount: currentPlayers.length,
      pot: 0,
      handNumber: 1,
      bettingRound: 1,
      handOpen: false,
      lastActionId: action.id,
      updatedAt: serverTimestamp(),
    })
    transaction.set(action, actionData({
      type: 'restart_game',
      actorUid: uid,
      actorName: actor.displayName,
      amount: game.pot,
      playerCount: currentPlayers.length,
      handNumber: 1,
    }))
  })
}

export async function startNextBettingRound(db: Firestore, code: string, uid: string, expectedRound?: number): Promise<void> {
  const room = gameRef(db, code)
  const action = newActionRef(db, code)

  await runTransaction(db, async (transaction) => {
    const [roomSnapshot, actor] = await Promise.all([
      transaction.get(room),
      readActor(transaction, db, code, uid),
    ])
    if (!roomSnapshot.exists()) throw new Error('Room no longer exists · החדר כבר אינו קיים.')
    const game = roomSnapshot.data() as Game
    requirePlaying(game)
    if (!canControlGame(game, uid)) throw new Error('Only the Leader can start a new betting round · רק המוביל/ה יכול/ה לפתוח סבב חדש.')
    if (game.handOpen === false) throw new Error('Hand closed; wait for a new hand · היד סגורה; המתינו ליד חדשה.')
    if (expectedRound != null && currentBettingRound(game) !== expectedRound) throw new Error('Betting round already changed · סבב ההימורים כבר השתנה.')
    const nextRound = currentBettingRound(game) + 1

    transaction.update(room, {
      bettingRound: nextRound,
      handOpen: true,
      lastActionId: action.id,
      updatedAt: serverTimestamp(),
    })
    transaction.set(action, actionData({
      type: 'new_round',
      actorUid: uid,
      actorName: actor.displayName,
      amount: nextRound,
      handNumber: game.handNumber,
    }))
  })
}

export async function contributeChips(
  db: Firestore,
  code: string,
  uid: string,
  amount: number,
  operationId?: string,
): Promise<void> {
  if (!Number.isSafeInteger(amount) || amount <= 0) throw new Error('Choose a valid chip amount · בחרו כמות חוקית של צ׳יפים.')
  const room = gameRef(db, code)
  const player = playerRef(db, code, uid)
  const action = newActionRef(db, code, operationId)

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      await runTransaction(db, async (transaction) => {
        const [roomSnapshot, playerSnapshot, actor, existingAction] = await Promise.all([
          transaction.get(room),
          transaction.get(player),
          readActor(transaction, db, code, uid),
          transaction.get(action),
        ])
        if (existingAction.exists()) {
          if (isMatchingContributionAction(existingAction.data() as GameAction, uid, amount)) return
          throw new Error('This contribution request was already used · בקשת התרומה הזאת כבר שימשה לפעולה אחרת.')
        }
        if (!roomSnapshot.exists() || !playerSnapshot.exists()) throw new Error('No active player found in this room · לא נמצא שחקן פעיל בחדר.')
        const game = roomSnapshot.data() as Game
        const currentPlayer = playerSnapshot.data() as Player
        requirePlaying(game)
        if (game.handOpen === false) throw new Error('Hand closed; wait for a new hand · היד נסגרה; המתינו ליד חדשה.')
        if (currentPlayer.foldedHand === game.handNumber) throw new Error('You already folded this hand · כבר פרשת מהיד הנוכחית.')
        if (amount > currentPlayer.balance) throw new Error('Not enough chips · אין מספיק צ׳יפים.')

        const currentContribution = currentPlayer.contributionHand === game.handNumber ? currentPlayer.handContribution : 0
        const round = currentBettingRound(game)
        const currentRoundContribution = currentPlayer.contributionHand === game.handNumber && currentPlayer.contributionRound === round
          ? (currentPlayer.roundContribution ?? 0)
          : 0
        transaction.update(player, {
          balance: currentPlayer.balance - amount,
          contributionHand: game.handNumber,
          handContribution: currentContribution + amount,
          contributionRound: round,
          roundContribution: currentRoundContribution + amount,
          updatedAt: serverTimestamp(),
        })
        transaction.update(room, {
          pot: game.pot + amount,
          handOpen: true,
          lastActionId: action.id,
          updatedAt: serverTimestamp(),
        })
        transaction.set(action, actionData({
          type: 'bet',
          actorUid: uid,
          actorName: actor.displayName,
          targetUid: uid,
          targetName: currentPlayer.name,
          amount,
          handNumber: game.handNumber,
        }))
      })
      return
    } catch (error) {
      const code = (error as { code?: string }).code
      const retryable = code === 'permission-denied' || code === 'aborted'
      if (!retryable) throw error

      // Concurrent rules evaluation can surface a conflict as permission-denied
      // before the SDK retries it. First verify whether this immutable operation
      // already won; otherwise rerun from fresh room/player snapshots.
      const existing = await getDoc(action)
      if (existing.exists()) {
        const data = existing.data() as GameAction
        if (isMatchingContributionAction(data, uid, amount)) return
        throw error
      }
      if (attempt === 2) throw error
    }
  }
}

export async function foldHand(db: Firestore, code: string, uid: string): Promise<void> {
  const room = gameRef(db, code)
  const player = playerRef(db, code, uid)
  const action = newActionRef(db, code)

  await runTransaction(db, async (transaction) => {
    const [roomSnapshot, playerSnapshot, actor] = await Promise.all([
      transaction.get(room),
      transaction.get(player),
      readActor(transaction, db, code, uid),
    ])
    if (!roomSnapshot.exists() || !playerSnapshot.exists()) throw new Error('No active player found in this room · לא נמצא שחקן פעיל בחדר.')
    const game = roomSnapshot.data() as Game
    const currentPlayer = playerSnapshot.data() as Player
    requirePlaying(game)
    if (game.handOpen === false) throw new Error('Hand closed; wait for a new hand · היד נסגרה; המתינו ליד חדשה.')
    if (currentPlayer.foldedHand === game.handNumber) throw new Error('You already folded this hand · כבר פרשת מהיד הנוכחית.')

    transaction.update(player, { foldedHand: game.handNumber, updatedAt: serverTimestamp() })
    transaction.update(room, { handOpen: true, lastActionId: action.id, updatedAt: serverTimestamp() })
    transaction.set(
      action,
      actionData({
        type: 'fold',
        actorUid: uid,
        actorName: actor.displayName,
        targetUid: uid,
        targetName: currentPlayer.name,
        handNumber: game.handNumber,
      }),
    )
  })
}

export async function awardPot(db: Firestore, code: string, uid: string, targetUid: string): Promise<void> {
  const room = gameRef(db, code)
  const target = playerRef(db, code, targetUid)
  const action = newActionRef(db, code)

  await runTransaction(db, async (transaction) => {
    const [roomSnapshot, targetSnapshot, actor] = await Promise.all([
      transaction.get(room),
      transaction.get(target),
      readActor(transaction, db, code, uid),
    ])
    if (!roomSnapshot.exists() || !targetSnapshot.exists()) throw new Error('Selected player was not found · השחקן שנבחר לא נמצא.')
    const game = roomSnapshot.data() as Game
    const targetPlayer = targetSnapshot.data() as Player
    requirePlaying(game)
    if (!canControlGame(game, uid)) throw new Error('Only the Leader or table can award the pot · רק המוביל/ה או השולחן יכולים להעניק את הקופה.')
    if (game.handOpen === false) throw new Error('Hand is already closed · היד כבר סגורה.')
    if (game.pot <= 0) throw new Error('Pot is empty · הקופה ריקה.')

    transaction.update(target, { balance: targetPlayer.balance + game.pot, updatedAt: serverTimestamp() })
    transaction.update(room, { pot: 0, handOpen: false, lastActionId: action.id, updatedAt: serverTimestamp() })
    transaction.set(
      action,
      actionData({
        type: 'award',
        actorUid: uid,
        actorName: actor.displayName,
        targetUid,
        targetName: targetPlayer.name,
        amount: game.pot,
        handNumber: game.handNumber,
      }),
    )
  })
}

export async function startNewHand(db: Firestore, code: string, uid: string, expectedHand?: number): Promise<void> {
  const room = gameRef(db, code)
  const action = newActionRef(db, code)

  await runTransaction(db, async (transaction) => {
    const [roomSnapshot, actor] = await Promise.all([
      transaction.get(room),
      readActor(transaction, db, code, uid),
    ])
    if (!roomSnapshot.exists()) throw new Error('Room no longer exists · החדר כבר אינו קיים.')
    const game = roomSnapshot.data() as Game
    requirePlaying(game)
    if (!canControlGame(game, uid)) throw new Error('Only the Leader or table can start a new hand · רק המוביל/ה או השולחן יכולים להתחיל יד חדשה.')
    const blockedReason = getNewHandBlockReason(game)
    if (blockedReason) throw new Error(blockedReason)
    if (expectedHand != null && game.handNumber !== expectedHand) throw new Error('A new hand already started · יד חדשה כבר התחילה.')

    transaction.update(room, {
      handNumber: game.handNumber + 1,
      bettingRound: 1,
      handOpen: true,
      lastActionId: action.id,
      updatedAt: serverTimestamp(),
    })
    transaction.set(
      action,
      actionData({
        type: 'new_hand',
        actorUid: uid,
        actorName: actor.displayName,
        handNumber: game.handNumber + 1,
      }),
    )
  })
}

export function getNewHandBlockReason(game: Pick<Game, 'handOpen' | 'pot'>): string | null {
  if (game.handOpen !== false) {
    return 'Finish the current hand before starting a new one · סיימו את היד הנוכחית לפני שמתחילים יד חדשה.'
  }
  if (game.pot !== 0) {
    return 'Award or correct the pot before a new hand · העניקו או תקנו את הקופה לפני יד חדשה.'
  }
  return null
}

export interface HandResetSummary {
  refundAmount: number
  playerCount: number
  matchesPot: boolean
}

export function getHandResetSummary(game: Game, players: Player[]): HandResetSummary {
  const currentContributions = players
    .filter((player) => player.contributionHand === game.handNumber && player.handContribution > 0)
  const refundAmount = currentContributions.reduce((sum, player) => sum + player.handContribution, 0)
  return {
    refundAmount,
    playerCount: currentContributions.length,
    matchesPot: refundAmount === game.pot,
  }
}

export async function resetCurrentHand(
  db: Firestore,
  code: string,
  uid: string,
  confirmedCode: string,
): Promise<HandResetSummary> {
  const normalizedCode = normalizeRoomCode(code)
  if (normalizeRoomCode(confirmedCode) !== normalizedCode) {
    throw new Error('Type the current 4-character room code to confirm · הקלידו את קוד החדר הנוכחי בן 4 התווים לאישור.')
  }

  const room = gameRef(db, normalizedCode)
  const action = newActionRef(db, normalizedCode)
  // Resolve the bounded player set first, then re-read every document inside the
  // transaction. A concurrent room change causes a retry or a safe pot mismatch.
  const listedPlayers = await getDocs(collection(db, 'games', normalizedCode, 'players'))
  if (listedPlayers.size > MAX_PLAYERS) throw new Error(`Reset supports up to ${MAX_PLAYERS} players · האיפוס תומך בעד ${MAX_PLAYERS} שחקנים.`)
  const playerDocuments = listedPlayers.docs.map((snapshot) => playerRef(db, normalizedCode, snapshot.id))

  return runTransaction(db, async (transaction) => {
    const [roomSnapshot, actor, ...playerSnapshots] = await Promise.all([
      transaction.get(room),
      readActor(transaction, db, normalizedCode, uid),
      ...playerDocuments.map((reference) => transaction.get(reference)),
    ])
    if (!roomSnapshot.exists()) throw new Error('Room no longer exists · החדר כבר אינו קיים.')
    const game = roomSnapshot.data() as Game
    requirePlaying(game)
    if (!canControlGame(game, uid)) throw new Error('Only the Leader or table can reset a hand · רק המוביל/ה או השולחן יכולים לאפס יד.')
    if (game.code !== normalizedCode) throw new Error('Room code does not match · קוד החדר אינו תואם.')
    if (game.handOpen === false) throw new Error('Hand is already closed · היד כבר סגורה.')
    if (game.pot <= 0) throw new Error('The pot is already empty; use NEW HAND · הקופה כבר ריקה; השתמשו ביד חדשה.')

    const currentPlayers = playerSnapshots.filter((snapshot) => snapshot.exists()).map((snapshot) => snapshot.data() as Player)
    const summary = getHandResetSummary(game, currentPlayers)
    if (!summary.matchesPot) {
      throw new Error('Pot does not match current contributions. Use FIX or START OVER; no chips were changed · הקופה אינה תואמת לתרומות ביד. השתמשו בתיקון או בהתחלה מחדש; הצ׳יפים לא שונו.')
    }

    const nextHand = game.handNumber + 1
    playerSnapshots.forEach((snapshot, index) => {
      if (!snapshot.exists()) return
      const player = snapshot.data() as Player
      const refund = player.contributionHand === game.handNumber ? player.handContribution : 0
      transaction.update(playerDocuments[index], {
        balance: player.balance + refund,
        foldedHand: null,
        contributionHand: nextHand,
        handContribution: 0,
        contributionRound: 1,
        roundContribution: 0,
        updatedAt: serverTimestamp(),
      })
    })
    transaction.update(room, {
      pot: 0,
      handNumber: nextHand,
      bettingRound: 1,
      handOpen: true,
      lastActionId: action.id,
      updatedAt: serverTimestamp(),
    })
    transaction.set(action, actionData({
      type: 'reset_hand',
      actorUid: uid,
      actorName: actor.displayName,
      amount: summary.refundAmount,
      playerCount: summary.playerCount,
      handNumber: nextHand,
    }))
    return summary
  })
}

export async function startOverGame(
  db: Firestore,
  uid: string,
  game: Game,
  tableDisplayName: string,
  confirmedCode: string,
): Promise<{ code: string; member: Member; recoveryCode: string }> {
  if (normalizeRoomCode(confirmedCode) !== game.code) {
    throw new Error('Type the current 4-character room code to confirm · הקלידו את קוד החדר הנוכחי בן 4 התווים לאישור.')
  }
  return createGame(db, uid, {
    gameName: game.name,
    displayName: tableDisplayName,
    startingStack: game.startingStack,
  })
}

export async function adjustBalance(
  db: Firestore,
  code: string,
  uid: string,
  targetUid: string,
  delta: number,
): Promise<void> {
  if (!Number.isSafeInteger(delta) || delta === 0) throw new Error('Correction must be a non-zero whole number · התיקון חייב להיות מספר שלם שאינו אפס.')
  const room = gameRef(db, code)
  const target = playerRef(db, code, targetUid)
  const action = newActionRef(db, code)

  await runTransaction(db, async (transaction) => {
    const [roomSnapshot, targetSnapshot, actor] = await Promise.all([
      transaction.get(room),
      transaction.get(target),
      readActor(transaction, db, code, uid),
    ])
    if (!roomSnapshot.exists() || !targetSnapshot.exists()) throw new Error('Selected player was not found · השחקן שנבחר לא נמצא.')
    const game = roomSnapshot.data() as Game
    const targetPlayer = targetSnapshot.data() as Player
    requirePlaying(game)
    if (game.tableUid !== uid) throw new Error('Only the table device can make corrections · רק מכשיר השולחן יכול לבצע תיקון.')
    if (targetPlayer.balance + delta < 0) throw new Error('Correction would create a negative balance · התיקון ייצור יתרה שלילית.')

    transaction.update(target, { balance: targetPlayer.balance + delta, updatedAt: serverTimestamp() })
    transaction.update(room, {
      handOpen: game.handOpen !== false,
      lastActionId: action.id,
      updatedAt: serverTimestamp(),
    })
    transaction.set(
      action,
      actionData({
        type: 'adjust_balance',
        actorUid: uid,
        actorName: actor.displayName,
        targetUid,
        targetName: targetPlayer.name,
        amount: delta,
        handNumber: game.handNumber,
      }),
    )
  })
}

export async function adjustPot(db: Firestore, code: string, uid: string, delta: number): Promise<void> {
  if (!Number.isSafeInteger(delta) || delta === 0) throw new Error('Correction must be a non-zero whole number · התיקון חייב להיות מספר שלם שאינו אפס.')
  const room = gameRef(db, code)
  const action = newActionRef(db, code)

  await runTransaction(db, async (transaction) => {
    const [roomSnapshot, actor] = await Promise.all([
      transaction.get(room),
      readActor(transaction, db, code, uid),
    ])
    if (!roomSnapshot.exists()) throw new Error('Room no longer exists · החדר כבר אינו קיים.')
    const game = roomSnapshot.data() as Game
    requirePlaying(game)
    if (game.tableUid !== uid) throw new Error('Only the table device can make corrections · רק מכשיר השולחן יכול לבצע תיקון.')
    if (game.handOpen === false) throw new Error('Start a new hand before correcting the pot · התחילו יד חדשה לפני תיקון הקופה.')
    if (game.pot + delta < 0) throw new Error('Correction would create a negative pot · התיקון ייצור קופה שלילית.')

    transaction.update(room, {
      pot: game.pot + delta,
      handOpen: true,
      lastActionId: action.id,
      updatedAt: serverTimestamp(),
    })
    transaction.set(
      action,
      actionData({
        type: 'adjust_pot',
        actorUid: uid,
        actorName: actor.displayName,
        amount: delta,
        handNumber: game.handNumber,
      }),
    )
  })
}

export async function rotateRecoveryCode(db: Firestore, code: string, uid: string): Promise<string> {
  const recoveryCode = generateRecoveryCode()
  const recoveryHash = await hashRecoveryCode(recoveryCode)
  const room = gameRef(db, code)
  const control = controlRef(db, code)
  const action = newActionRef(db, code)

  await runTransaction(db, async (transaction) => {
    const [roomSnapshot, actor] = await Promise.all([
      transaction.get(room),
      readActor(transaction, db, code, uid),
    ])
    if (!roomSnapshot.exists()) throw new Error('Room no longer exists · החדר כבר אינו קיים.')
    const game = roomSnapshot.data() as Game
    if (game.tableUid !== uid) throw new Error('Only the table device can renew recovery · רק מכשיר השולחן יכול לחדש קוד שחזור.')

    transaction.set(control, { recoveryHash, updatedAt: serverTimestamp() })
    transaction.update(room, {
      handOpen: game.handOpen !== false,
      lastActionId: action.id,
      updatedAt: serverTimestamp(),
    })
    transaction.set(action, actionData({
      type: 'set_recovery',
      actorUid: uid,
      actorName: actor.displayName,
      handNumber: game.handNumber,
    }))
  })

  return recoveryCode
}

export async function recoverTableAuthority(
  db: Firestore,
  uid: string,
  input: RecoverTableInput,
): Promise<Member> {
  const code = normalizeRoomCode(input.code)
  const displayName = cleanName(input.displayName)
  const recoveryCode = normalizeRecoveryCode(input.recoveryCode)
  if (code.length !== 4) throw new Error('Room code must contain 4 characters · קוד החדר צריך להכיל 4 תווים.')
  if (!displayName) throw new Error('Enter a table-device name · הזינו שם למכשיר השולחן.')
  const recoveryHash = await hashRecoveryCode(recoveryCode)

  const room = gameRef(db, code)
  const member = memberRef(db, code, uid)
  const action = newActionRef(db, code)
  // The claim ID deliberately matches the immutable recovery action. Rules can
  // therefore bind the secret proof, authority transfer and audit record into
  // one atomic transaction without exposing the stored recovery hash.
  const claim = recoveryClaimRef(db, code, action.id)

  return runTransaction(db, async (transaction) => {
    const [roomSnapshot, memberSnapshot] = await Promise.all([
      transaction.get(room),
      transaction.get(member),
    ])
    if (!roomSnapshot.exists()) throw new Error('No game found for this code · לא נמצא משחק עם הקוד הזה.')
    const game = roomSnapshot.data() as Game
    if (game.tableUid === uid) throw new Error('This device already controls the table · המכשיר הזה כבר שולט בשולחן.')

    const existingMember = memberSnapshot.exists() ? (memberSnapshot.data() as Member) : null
    const recoveredMember: Member = existingMember ?? {
      uid,
      displayName,
      role: 'table',
      isPlayer: false,
      joinedAt: null,
    }
    if (!existingMember) {
      transaction.set(member, {
        uid,
        displayName,
        role: 'table',
        isPlayer: false,
        joinedAt: serverTimestamp(),
      })
    }
    transaction.set(claim, { uid, recoveryHash, createdAt: serverTimestamp() })
    transaction.update(room, {
      tableUid: uid,
      handOpen: game.handOpen !== false,
      lastActionId: action.id,
      updatedAt: serverTimestamp(),
    })
    transaction.set(action, actionData({
      type: 'reclaim_table',
      actorUid: uid,
      actorName: recoveredMember.displayName,
      targetUid: uid,
      targetName: recoveredMember.displayName,
      handNumber: game.handNumber,
    }))

    return recoveredMember
  })
}

export async function undoLastAction(db: Firestore, code: string, uid: string): Promise<void> {
  const room = gameRef(db, code)
  const undoAction = newActionRef(db, code)

  await runTransaction(db, async (transaction) => {
    const [roomSnapshot, actor] = await Promise.all([
      transaction.get(room),
      readActor(transaction, db, code, uid),
    ])
    if (!roomSnapshot.exists()) throw new Error('Room no longer exists · החדר כבר אינו קיים.')
    const game = roomSnapshot.data() as Game
    if (game.tableUid !== uid) throw new Error('Only the table device can undo · רק מכשיר השולחן יכול לבטל פעולה.')
    if (game.handOpen === false) throw new Error('Hand closed; use table correction · היד נסגרה; השתמשו בתיקון שולחן.')

    const latestRef = doc(db, 'games', normalizeRoomCode(code), 'actions', game.lastActionId)
    const latestSnapshot = await transaction.get(latestRef)
    if (!latestSnapshot.exists()) throw new Error('Latest action is unavailable · הפעולה האחרונה אינה זמינה.')
    const latest = { id: latestSnapshot.id, ...latestSnapshot.data() } as GameAction
    if (latest.handNumber !== game.handNumber || (latest.type !== 'bet' && latest.type !== 'fold')) {
      throw new Error('Only the latest bet or fold in this hand can be undone · רק התרומה או הכיפול האחרונים ביד ניתנים לביטול.')
    }

    const target = playerRef(db, code, latest.targetUid)
    const targetSnapshot = await transaction.get(target)
    if (!targetSnapshot.exists()) throw new Error('Player from the latest action is unavailable · השחקן מהפעולה האחרונה אינו זמין.')
    const targetPlayer = targetSnapshot.data() as Player

    if (latest.type === 'bet') {
      const currentContribution = targetPlayer.contributionHand === game.handNumber ? targetPlayer.handContribution : 0
      const round = currentBettingRound(game)
      const currentRoundContribution = targetPlayer.contributionHand === game.handNumber && targetPlayer.contributionRound === round
        ? (targetPlayer.roundContribution ?? 0)
        : 0
      if (game.pot < latest.amount || currentContribution < latest.amount) {
        throw new Error('A dependent action already occurred; use correction · כבר בוצעה פעולה תלויה; השתמשו בתיקון.')
      }
      if (currentRoundContribution < latest.amount) {
        throw new Error('The contribution belongs to a previous betting round · התרומה שייכת לסבב הימורים קודם.')
      }
      transaction.update(target, {
        balance: targetPlayer.balance + latest.amount,
        handContribution: currentContribution - latest.amount,
        roundContribution: currentRoundContribution - latest.amount,
        updatedAt: serverTimestamp(),
      })
      transaction.update(room, {
        pot: game.pot - latest.amount,
        handOpen: true,
        lastActionId: undoAction.id,
        updatedAt: serverTimestamp(),
      })
      transaction.set(undoAction, actionData({
        type: 'undo_bet',
        actorUid: uid,
        actorName: actor.displayName,
        targetUid: targetPlayer.uid,
        targetName: targetPlayer.name,
        amount: latest.amount,
        handNumber: game.handNumber,
        reversesActionId: latest.id,
      }))
      return
    }

    if (targetPlayer.foldedHand !== game.handNumber) throw new Error('Fold is no longer current · הכיפול כבר אינו המצב הנוכחי.')
    transaction.update(target, { foldedHand: null, updatedAt: serverTimestamp() })
    transaction.update(room, {
      handOpen: true,
      lastActionId: undoAction.id,
      updatedAt: serverTimestamp(),
    })
    transaction.set(undoAction, actionData({
      type: 'undo_fold',
      actorUid: uid,
      actorName: actor.displayName,
      targetUid: targetPlayer.uid,
      targetName: targetPlayer.name,
      handNumber: game.handNumber,
      reversesActionId: latest.id,
    }))
  })
}

export function readableFirebaseError(error: unknown): string {
  if (error instanceof Error) {
    if (error.message.includes('Missing or insufficient permissions')) {
      return text('Security rules rejected the action; refresh and retry.', 'כללי האבטחה דחו את הפעולה; רעננו ונסו שוב.')
    }
    if (error.message.toLowerCase().includes('network')) return text('No network connection; retry when online.', 'אין חיבור לרשת; נסו שוב כשהחיבור יחזור.')
    if (error.message.includes(' · ')) return pickBilingual(error.message)
    return text('Something went wrong; try again.', 'משהו השתבש; נסו שוב.')
  }
  return text('Something went wrong; try again.', 'משהו השתבש; נסו שוב.')
}
